import {
    addErrorAlert,
    addInterval,
    closeRFIDReader,
    getCafaConf,
    getQuickPosProducts,
    setScreenSaver
} from "../redux/actions";
import {
    CAFA_CONF_SET,
    SET_DECATHLON_THEME,
    SET_PLUGIN_URLS,
    SET_POS_UI_CUSTOMIZATION,
    SET_UI_CUSTOMIZATION
} from "../redux/actionTypes";
import {SET_INTEGRATION} from "../redux/payment/actionTypes";
import {login as paymentLogin, setZipCodeSurveyCounter} from "../redux/payment/actions";
import packageInfo from "../../package.json";
import {clearAppPreCache, getVersionParamFromURL, handleHardReload} from "../util/helperFunctions";
import {localeAdded, languagesSet, localeSelected} from "localization/localizeSlice";
import * as Sentry from "@sentry/react";
import * as amplitude from '@amplitude/analytics-browser';
import CTR from "../util/receiptTemplate";
import moment from "moment/moment";

const initSentry = () => {
    if(window.AppConf.sendSentryLogs !== false){
        console.log("Init sentry");
        Sentry.init({
            dsn: "https://76b5b9d4b5474b5f8f6e68e5f4b4cb88@sentry.nimi24.com/42",
            release: "SCO@" + (window.AppConf?.version ?? ""),
            beforeSend(event, hint) {
                console.log('Sentry: beforeSend', {event, hint});
                const error = hint.originalException;
                if (
                    error &&
                    error.message &&
                    error.message.match(/Cannot read property 'scrollTop' of null/i)
                ) {
                    return null;
                }
                return event;
            },
        });
    }
}

const initAmplitude = () => {
    if((window.AppConf?.amplitude?.enabled ?? false) !== false){
        console.log("Init Amplitude");
        let conf = window.AppConf.amplitude;
        amplitude.init(conf.key, {
            serverZone: conf.serverZone,
            defaultTracking: false
        });
        amplitude.track("app_launched");
    }
}

let translations = false;
export const addTranslations = (dispatch, languages) => {
    const applyTranslations = () => {
        for(let language of languages){
            const translation = translations.find(el => el.name === language);
            console.log('translation', {language, translation});
            if(typeof translation !== "undefined"){
                dispatch(localeAdded({name: language, messages: translation.value}));
            }else{
                fetch(process.env.PUBLIC_URL + '/translations/' + language + '.json').then(response => response.json()).then(response=>{
                    dispatch(localeAdded({name: language, messages: response}));
                }).catch(() => {});
            }
        }
    }

    if(typeof process?.env?.REACT_APP_LANGUAGE_URL === "undefined"){
        translations = [];
    }

    if(translations === false){
        fetch(process.env.REACT_APP_LANGUAGE_URL, {
            headers: {
                jwt: process.env.REACT_APP_LANGUAGE_JWT
            }
        }).then(response => response.json()).then(response => {
            if(Array.isArray(response)){
                translations = response.filter(el => el.type === 'translation');
            }else{
                translations = [];
            }
            applyTranslations();
        }).catch(() => {
            console.log("Getting translations failed");
            translations = [];
            applyTranslations();
        });
    }else{
        applyTranslations();
    }
}

const getBoolean = (value) => {
    return value === "1";
}

const setAppVersion = (version, posID = false, dispatch = false, operationMode = false) => {
    const currentVersion = getVersionParamFromURL();
    if(version !== currentVersion && window.location.origin.indexOf('localhost') === -1){
        if(posID !== false){
            localStorage.setItem('autoLoginPOSID', posID);
            localStorage.setItem('autoLoginOperationMode', operationMode);
            localStorage.setItem('currentPOSVersion', version);
        }

        const reload = () => {
            if(version === 'latest'){
                handleHardReload(window.location.origin + window.location.search, true);
            }else{
                handleHardReload(window.location.origin + '/' + version + '/' + window.location.search, currentVersion !== "latest");
            }
            clearAppPreCache(version);
        }
        if(dispatch !== false){
            dispatch(closeRFIDReader(() => {
                reload();
            }));
        }else{
            reload();
        }
    }
}

export const checkLocalAppVersion = () => {
    const localVersion = localStorage.getItem('currentPOSVersion') || false;
    if(localVersion !== false){
        const currentVersion = getVersionParamFromURL();
        if(currentVersion !== localVersion){
            setAppVersion(localVersion);
        }
    }
}

export const setDefaultAppConf = () => {
    let preprod = typeof process.env.REACT_APP_PREPROD !== "undefined" ?
        process.env.REACT_APP_PREPROD === "1" :
        window.location.origin.indexOf("sco.erply.com") === -1
    window.AppConf = {
        version: packageInfo.version,
        defaultConf: true,
        plugins: [],
        preprod,
        languages: ['en'],
        enableRFID: false,
        handleSecurityTagsAfterScanning: false,
        EFT: 'demo',
        paymentTypes: {
            integration: [],
            manualInput: [],
            voucher: []
        },
        voucherProducts: [],
        manualVoucherInput: [],
        giftCardItemCode: "0000000",
        completeTransactionOnGiftCardActivationFail: false,
        transactionTimeout: 120,
        scanCustomerCardTimeout: 0,
        showNoReceiptButton: true,
        printReceiptOnTimeout: false,
        receiptTimeout: 15,
        mixedTransaction: true,
        showPrintingDialog: true,
        printDecathlonReceipt: process.env.REACT_APP_ERPLY_MODE !== "1",
        decathlonQRCodeLogin: true,
        decathlonSSOQRCodeURL: process.env.REACT_APP_DECATHLON_SSO_QR_CODE_URL || false,
        decathlonQRCodeSupervisor: false,
        ignoreDiscountableFlag: true,
        showHangerGif:true,
        scanDecathlonCardGif: false,
        sendSentryLogs: false,
        usedProductConfirm: true,
        fullscreenCustomerSearch: false,
        unifiedVersion: true,
        customReturnReasonCodes: false,
        cashOutLimit: false,
        cashInLimit: false,
        emailIncludeGiftReceipt: false,
        selfValidateSupervisor: false,
        printerTimeoutPrintDays: [0,0,0,0,0,0,0],
        discountSupervisorNoReturnLimit: false,
        displayNetTotal: false,
        showCashChangeOnReceipt: true,
        enableYocuda: false,
        enableErplyPromoCode: false,
        enableSmartProductReturn: false,
        enable2ndLifePriceButton: false,
        enableVerifiedReturn: false,
        verifiedReturn: {
            override: false,
            allowDifferentMOP: false,
            allowedAltMOPs: [], //List of reason codes
            manual: false,
            manualSupervisor: true,
            unverifiedSupervisor: false,
            unverifiedSupervisorSelfValidate: true
        },
        enableCardTypeSelectForManualCard: false,
        pluginMandatory: false,
        productRemoveSupervisor: false,
        preventPaymentRoundingForAmountLessThanRounding: false,
        keepCancelledProductsInBasket: false,
        enableEPCProductApiSearch: false,
        enableEPCProductApiSearchOnPayment: false,
        feedbackApp: {
            responseUrl: "",
            customer: {
                enabled: false,
                timeout: "",
                url: ""
            },
            employee: {
                enabled: false,
                timeout: "",
                url: ""
            }
        },
        customerOptInOnIdentification: true,
        showOptInIncentives: false,
        returnVoucherHasNetPrice: false,
        showPaymentRoundingOnReceipt: false,
        cardTypePrefix: ""
    };
    if(typeof process.env.REACT_APP_POSLOG_URL !== "undefined"){
        window.AppConf.POSLog = {
            url: process.env.REACT_APP_POSLOG_URL
        }
    }
    if(typeof process.env.REACT_APP_DECATHLON_CRM_URL !== "undefined"){
        window.AppConf.CustomerAPI = {
            url: process.env.REACT_APP_DECATHLON_CRM_URL
        }
    }
    if(typeof process.env.REACT_APP_ONEPAY_URL !== "undefined"){
        window.AppConf.FirstData = {
            url: process.env.REACT_APP_ONEPAY_URL
        }
    }
}

const mergeConf = (conf, posID) => {
    let merged = conf.reduce((acc, el) => {
        let isForPos =  el.level === 'Company' || (el.level === 'Pos' && el.level_id === posID);
        if(isForPos){
            if(typeof acc[el.name] === "undefined"){
                acc[el.name] = el;
            }else{
                if(acc[el.name].level === 'Company' && el.level === 'Pos'){
                    acc[el.name] = el;
                }
            }
        }
        return acc;
    }, {});
    return Object.values(merged);
}
export default (dispatch, pos, operationMode, erplyConf) => {
    return new Promise((resolve, reject) => {
        if(window.AppConf.defaultConf){
            dispatch(getCafaConf( (data) => {
                console.log("CAFA_CONF", data);
                if(data === false || data === null){
                    reject();
                    return addErrorAlert('No data from CAFA API!');
                }

                const applyConfigurations = (configurations) => {
                    for(let conf of configurations){
                        switch (conf.name){
                            case 'languages': {
                                try {
                                    let languages = conf.value.split(',');
                                    dispatch(languagesSet(languages));
                                    dispatch(localeSelected(languages[0]));
                                    addTranslations(dispatch, languages);
                                    let translationsLoadInterval = setInterval(() => {
                                        console.log("fetch languages");
                                        translations = false;
                                        addTranslations(dispatch, languages);
                                    }, 1800000);
                                    dispatch(addInterval(translationsLoadInterval));
                                }catch (e){}
                                break;
                            }
                            case 'plugins': {
                                if(conf.value !== ""){
                                    dispatch({
                                        type: SET_PLUGIN_URLS,
                                        payload: conf.value.split(',')
                                    });
                                }
                                break;
                            }
                            case 'EFT':
                                if(conf.value !== ""){
                                    dispatch({
                                        type: SET_INTEGRATION,
                                        payload: conf.value
                                    });
                                    window.AppConf[conf.name] = conf.value;
                                }
                                break;
                            case 'voucherProducts': {
                                window.AppConf[conf.name] = Object.values(conf.value);
                                break;
                            }
                            case 'manualVoucherInput': {
                                window.AppConf[conf.name] = conf.value.split(',');
                                break;
                            }
                            case 'paymentTypeMain': {
                                window.AppConf.paymentTypes.main = Object.keys(conf.value).reduce((acc, el) => {
                                    acc[el] = Object.keys(conf.value[el]).reduce((accConf, elConf) => {
                                        let elConfValue = conf.value[el][elConf];
                                        accConf[elConf] = typeof elConfValue === "string" && elConfValue.length === 1 ? getBoolean(elConfValue) : elConfValue;
                                        return accConf;
                                    }, {});
                                    return acc;
                                }, {});
                                break;
                            }
                            case 'paymentTypeGiftCard': {
                                window.AppConf.paymentTypes.giftCard = getBoolean(conf.value);
                                break;
                            }
                            case 'paymentTypeCard': {
                                window.AppConf.paymentTypes.card = Object.values(conf.value);
                                break;
                            }
                            case 'paymentTypeCash': {
                                window.AppConf.paymentTypes.cash = getBoolean(conf.value);
                                break;
                            }
                            case 'paymentTypeIntegration': {
                                window.AppConf.paymentTypes.integration = Object.values(conf.value);
                                break;
                            }
                            case 'paymentTypeManualInput': {
                                window.AppConf.paymentTypes.manualInput = Object.values(conf.value);
                                break;
                            }
                            case 'paymentTypeVoucher': {
                                window.AppConf.paymentTypes.voucher = Object.values(conf.value);
                                break;
                            }
                            case 'preprod':
                            case 'handleSecurityTagsAfterScanning':
                            case 'completeTransactionOnGiftCardActivationFail':
                            case 'showNoReceiptButton':
                            case 'printReceiptOnTimeout':
                            case 'showPrintingDialog':
                            case 'alwaysPrintReceipt':
                            case 'mixedTransaction':
                            case 'ignoreDiscountableFlag':
                            case 'displayNetPrices':
                            case 'showHangerGif':
                            case 'scanDecathlonCardGif':
                            case 'decathlonQRCodeSupervisor':
                            case 'sendSentryLogs':
                            case 'usedProductConfirm':
                            case 'fullscreenCustomerSearch':
                            case 'allowGiftCardPaymentWithGiftCard':
                            case 'addBagPrompt':
                            case 'customReturnReasonCodes':
                            case 'showQuickSelectProducts':
                            case 'posSpecificQuickSelectProducts':
                            case 'showProductGroups':
                            case 'enableManualCustomerSearch':
                            case 'productDescriptionFromAttributes':
                            case 'showProductSearch':
                            case 'emailIncludeGiftReceipt':
                            case 'selfValidateSupervisor':
                            case 'enableYocuda':
                            case 'yocudaSendAll':
                            case 'enableOnePromotion':
                            case 'onePromotionCouponEnabled':
                            case 'shortXReport':
                            case 'discountSupervisorNoReturnLimit':
                            case 'displayNetTotal':
                            case 'removePendingSalesBeforeCloseDay':
                            case 'showCashChangeOnReceipt':
                            case 'printDecathlonReceipt':
                            case 'enableErplyPromoCode':
                            case 'useDigitalReceipt':
                            case 'enableAddCustomer':
                            case 'displayEarnCustomerPoints':
                            case 'useWorkshopAPI':
                            case 'insuranceProductPrompt':
                            case 'enableSmartProductReturn':
                            case 'enable2ndLifePriceButton':
                            case 'enableVerifiedReturn':
                            case 'enableCardTypeSelectForManualCard':
                            case 'pluginMandatory':
                            case 'productRemoveSupervisor':
                            case 'preventPaymentRoundingForAmountLessThanRounding':
                            case 'keepCancelledProductsInBasket':
                            case 'enableEPCProductApiSearch':
                            case 'enableEPCProductApiSearchOnPayment':
                            case 'customerOptInOnIdentification':
                            case 'showOptInIncentives':
                            case 'returnVoucherHasNetPrice':
                            case 'showPaymentRoundingOnReceipt':
                            case 'enableRFID': {
                                window.AppConf[conf.name] = getBoolean(conf.value);
                                break;
                            }
                            case 'showEmergencyCashDrawerButton': {
                                let showEmergencyCashDrawerButton = getBoolean(conf.value);
                                window.AppConf[conf.name] = showEmergencyCashDrawerButton;
                                localStorage.setItem("showEmergencyCashDrawerButton", showEmergencyCashDrawerButton ? "1":"0");
                                break;
                            }
                            case 'emergencyCashDrawerButtonLogEmails': {
                                window.AppConf[conf.name] = conf.value;
                                localStorage.setItem("emergencyCashDrawerButtonLogEmails", conf.value);
                                break;
                            }
                            case 'useZipCodeSurvey': {
                                window.AppConf[conf.name] = getBoolean(conf.value);
                                if( window?.AppConf?.useZipCodeSurvey &&
                                    erplyConf.DKTZipSurveyFrequency !== "undefined"
                                ){
                                    dispatch(setZipCodeSurveyCounter(1));
                                }
                                break;
                            }
                            case 'priceDecimals':
                            case 'giftCardActivationLimit':
                            case 'scanCustomerCardTimeout':
                            case 'receiptTimeout':
                            case 'transactionProductLineLimit':
                            case 'transactionTimeout': {
                                if(conf.value !== ""){
                                    if(!isNaN(parseInt(conf.value))){
                                        window.AppConf[conf.name] = parseInt(conf.value);
                                    }
                                }
                                break;
                            }
                            case 'cashOutLimit':
                            case 'cashInLimit': {
                                if(conf.value !== ""){
                                    let numberValue = parseFloat(conf.value.replace(',','.'));
                                    if(!isNaN(numberValue)){
                                        window.AppConf[conf.name] = numberValue;
                                    }
                                }
                                break;
                            }
                            case 'appVersion': {
                                break;
                            }
                            case 'payback-settings': {
                                if(typeof conf.value?.credentials !== "undefined"){
                                    window.AppConf['payback'] = conf.value.credentials;
                                    window.AppConf['payback'].url = conf.value.credentials.inputUrl;

                                }
                                break;
                            }
                            case 'UICustomization': {
                                if(process.env.REACT_APP_ERPLY_MODE === "1"){
                                    let uiCustomization = conf.value;
                                    uiCustomization.saved = true;
                                    dispatch({
                                        type: SET_UI_CUSTOMIZATION,
                                        payload: uiCustomization
                                    })
                                }
                                break;
                            }
                            case 'UICustomizationPOS': {
                                if(process.env.REACT_APP_ERPLY_MODE === "1"){
                                    let uiCustomization = conf.value;
                                    uiCustomization.saved = true;
                                    dispatch({
                                        type: SET_POS_UI_CUSTOMIZATION,
                                        payload: uiCustomization
                                    });
                                }
                                break;
                            }
                            case 'decathlonTheme': {
                                dispatch({
                                    type: SET_DECATHLON_THEME,
                                    payload: conf.value
                                });
                                if(conf.value === 'blue'){
                                    CTR.logoBase64 = "data:image/png;base64,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";
                                }
                                break;
                            }
                            case 'screenSaverImageUrl':
                            case 'screenSaverImageTextColor': {
                                if(conf.value !== ""){
                                    window.AppConf[conf.name] = conf.value;
                                }
                                break;
                            }
                            default: {
                                window.AppConf[conf.name] = conf.value;
                            }

                        }
                    }
                }

                applyConfigurations(mergeConf(data, pos.pointOfSaleID.toString()));

                if(typeof window.AppConf.showEmergencyCashDrawerButton === "undefined"){
                    localStorage.removeItem("showEmergencyCashDrawerButton");
                }
                if(typeof window.AppConf.emergencyCashDrawerButtonLogEmails === "undefined"){
                    localStorage.removeItem("emergencyCashDrawerButtonLogEmails");
                }

                initSentry();
                initAmplitude();

                let appVersion = data.find(el => {
                    return el.name === 'appVersion' && el.level === 'Pos' && el.level_id === pos.pointOfSaleID.toString();
                });
                if(typeof appVersion === "undefined"){
                    appVersion = data.find(el => {
                        return el.name === 'appVersion' && el.level === 'Company';
                    });
                }
                if(typeof appVersion !== "undefined"){
                    setAppVersion(appVersion.value, pos.pointOfSaleID, dispatch, operationMode);
                }

                if(process.env.REACT_APP_ERPLY_MODE === "1") {
                    dispatch(getQuickPosProducts(pos.warehouseID, pos.pointOfSaleID));
                }

                dispatch(paymentLogin());

                let images = [];
                let screenSaverImageDates = window.AppConf?.screenSaverImageDates ?? {
                    "1": {end:"",start:""},
                    "2": {end:"",start:""},
                    "3": {end:"",start:""}
                }
                let imageActive = (number) => {
                    let today = moment().startOf('day');
                    let start = moment(screenSaverImageDates[number].start).startOf('day');
                    if(!start.isValid()){
                        start = today;
                    }
                    let end = moment(screenSaverImageDates[number].end).endOf('day');
                    if(!end.isValid()){
                        end = today;
                    }
                    console.log({today, start, end, isBetween: today.isBetween(start, end, null, '[]')});
                    return today.isBetween(start, end, null, '[]');
                }
                if((window.AppConf?.screenSaverImageUrl ?? "") !== "" && imageActive(1)){
                        images.push({
                            url: window.AppConf.screenSaverImageUrl,
                            textColor: window.AppConf?.screenSaverImageTextColor ?? "fff"
                        });
                    }
                if((window.AppConf?.screenSaverImageUrl2 ?? "") !== "" && imageActive(2)){
                        images.push({
                            url: window.AppConf.screenSaverImageUrl2,
                            textColor: window.AppConf?.screenSaverImageTextColor2 ?? "fff"
                        });
                    }
                if((window.AppConf?.screenSaverImageUrl3 ?? "") !== "" && imageActive(3)){
                    images.push({
                        url: window.AppConf.screenSaverImageUrl3,
                        textColor: window.AppConf?.screenSaverImageTextColor3 ?? "fff"
                    });
                }
                if(images.length > 0){
                    dispatch(setScreenSaver(images));
                }

                resolve();
                return {
                    type: CAFA_CONF_SET
                }
            }, reject));
        }else{
            resolve();
        }
    });
}